import { Pressable } from "@-/com.npmjs.react-native";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { FrostedSurface } from "@-/dev.improve.haniverse.components.ui.frosted-surface";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@-/dev.improve.haniverse.components.ui.dropdown-menu";
import {
  GraphemeMorphemePanelsContext,
  MorphemePanelsContext,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._context";
import { useAddMorphemeToCollection } from "#use-add-morpheme-to-collection";
import {
  copyMorphemeToClipboard,
  useMnemonicFeedbackSheetLauncher,
} from "#use-mnemonic-feedback-sheet-launcher";
import { useMorphemeFavoriteToggle } from "#use-morpheme-favorite-toggle";

export function MorphemeFlashcardActionsMenuButton({ diameter }: { diameter: number }) {
  const [character, catalogMnemonic, ownMnemonic] = useContextSelectors(
    GraphemeMorphemePanelsContext,
    (c) => c.readonlySlice.data.grapheme,
    (c) => c.readonlySlice.data.mnemonic?.trim() ?? "",
    (c) => c.userSlice.data.meaningCard?.description?.trim() ?? "",
  );
  const [editAction, onFlashcardEditPendingChange] = useContextSelectors(
    MorphemePanelsContext,
    (c) => c.toolbarEditAction,
    (c) => c.onFlashcardEditPendingChange,
  );
  const favorite = useMorphemeFavoriteToggle();
  const collections = useAddMorphemeToCollection();
  const feedbackSheet = useMnemonicFeedbackSheetLauncher({
    character,
    currentMnemonic: ownMnemonic.length > 0 ? ownMnemonic : catalogMnemonic,
  });
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Pressable
            accessibilityLabel="Flashcard actions"
            accessibilityRole="button"
            testID="morpheme-card-actions-menu"
          >
            <FrostedSurface
              className="items-center justify-center rounded-full"
              style={{ height: diameter, width: diameter }}
            >
              <Icon className="text-foreground" name="LuMoreHorizontal" size={20} />
            </FrostedSurface>
          </Pressable>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" side="top" sideOffset={9}>
          {editAction === null ? (
            // Details publishes no edit action: open the meaning reading and edit there.
            onFlashcardEditPendingChange === undefined ? null : (
              <DropdownMenuItem onPress={() => onFlashcardEditPendingChange(true)}>
                <Icon name="LuPenLine" size={18} />
                <Text>Edit Flashcard</Text>
              </DropdownMenuItem>
            )
          ) : (
            <DropdownMenuItem onPress={editAction.onPress}>
              <Icon name={editAction.isEditing ? "LuCheck" : "LuPenLine"} size={18} />
              <Text>{editAction.isEditing ? "Finish Editing Flashcard" : "Edit Flashcard"}</Text>
            </DropdownMenuItem>
          )}
          <DropdownMenuCheckboxItem
            checked={favorite.isFavorited}
            disabled={favorite.isPending}
            onCheckedChange={favorite.toggle}
          >
            <Text>Favorite</Text>
          </DropdownMenuCheckboxItem>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <Icon name="MdPlaylistAdd" size={18} />
              <Text>Add to Collection</Text>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {collections.collections.length === 0 ? (
                <DropdownMenuItem disabled>
                  <Text>No Collections</Text>
                </DropdownMenuItem>
              ) : (
                collections.collections.map((collection) => (
                  <DropdownMenuCheckboxItem
                    checked={collection.isMember}
                    disabled={collection.isMember || collections.isPending}
                    key={collection.id}
                    onCheckedChange={() => collections.add(collection.id)}
                  >
                    <Text>{collection.name}</Text>
                  </DropdownMenuCheckboxItem>
                ))
              )}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuSeparator />
          <DropdownMenuItem onPress={() => copyMorphemeToClipboard(character)}>
            <Icon name="MdContentCopy" size={18} />
            <Text>{`Copy ${character}`}</Text>
          </DropdownMenuItem>
          <DropdownMenuItem onPress={feedbackSheet.open}>
            <Icon name="LuMessageSquare" size={18} />
            <Text>Give Feedback</Text>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {feedbackSheet.sheet}
    </>
  );
}
